import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Bell, Trophy, AlertTriangle, Info, CheckCircle, X } from "lucide-react";
import { useTranslation } from "react-i18next";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function NotificationBell({ gameId }) {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const panelRef = useRef(null);

  // Load stored notifications
  useEffect(() => {
    if (gameId) fetchNotifications();
  }, [gameId]);

  // WebSocket connection
  useEffect(() => {
    if (!gameId) return;

    const wsProtocol = BACKEND_URL?.startsWith("https") ? "wss" : "ws";
    const wsHost = BACKEND_URL?.replace(/^https?:\/\//, "");
    const wsUrl = `${wsProtocol}://${wsHost}/ws/${gameId}`;

    const connect = () => {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setNotifications((prev) => [data, ...prev]);
          setUnreadCount((prev) => prev + 1);
        } catch {
          // Malformed WS message — skip silently
        }
      };

      ws.onerror = (err) => {
        if (process.env.NODE_ENV === "development") {
          console.warn("WS connection error:", err?.type);
        }
      };

      ws.onclose = (event) => {
        if (event.code !== 1000) {
          // Abnormal close — reconnect after 5 seconds
          setTimeout(() => {
            if (wsRef.current === ws) connect();
          }, 5000);
        }
      };
    };

    connect();

    return () => {
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [gameId]);

  // Click outside to close
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications/${gameId}?limit=20`);
      setNotifications(res.data);
      setUnreadCount(res.data.filter((n) => !n.read).length);
    } catch {
      // Non-critical — notifications will retry on next poll
    }
  };

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // Non-critical — mark-read failure doesn't affect UX
    }
  };

  const markAllRead = () => {
    notifications.forEach((n) => {
      if (!n.read) markRead(n.id);
    });
  };

  const typeIcon = (type) => {
    if (type === "achievement") return <Trophy className="w-4 h-4 text-amber-500" />;
    if (type === "warning") return <AlertTriangle className="w-4 h-4 text-rose-500" />;
    if (type === "success") return <CheckCircle className="w-4 h-4 text-emerald-500" />;
    return <Info className="w-4 h-4 text-sky-500" />;
  };

  const timeAgo = (isoStr) => {
    if (!isoStr) return "";
    const diff = Date.now() - new Date(isoStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => { setOpen(!open); }}
        className="relative p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
            <p className="text-sm font-semibold text-slate-800">{t("notifications.title")}</p>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-sky-600 hover:text-sky-700 font-medium"
                data-testid="mark-all-read-btn"
              >
                {t("notifications.markAllRead")}
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">{t("notifications.noNotifications")}</p>
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer ${
                    !n.read ? "bg-sky-50/50" : ""
                  }`}
                  onClick={() => markRead(n.id)}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="mt-0.5">{typeIcon(n.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!n.read ? "font-medium text-slate-900" : "text-slate-700"}`}>
                      {n.title}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                  {!n.read && <div className="w-2 h-2 rounded-full bg-sky-500 mt-1.5 shrink-0" />}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
